import { IconButton } from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { usePreview } from "../components/PreviewContext";
import HighlightAltIcon from "@mui/icons-material/HighlightAlt";
import { css } from "styled-components";
import ResponsiveDropDown from "./ResponsiveDropDown";
import { useSelector } from "react-redux";
import TemplateVersionsDropDown from "./TemplateVersionsDropDown";
import { useMemo } from "react";

const Container = styled.div`
  display: flex;
  flex: 0 1 100%;
  flex-direction: column;
  height: 100%;
  min-width: 0;

  top-bar {
    display: flex;
    align-items: center;
    height: 48px;
    background: white;
  }

  iframe-container {
    display: flex;
    flex: 0 1 100%;
    min-width: 0;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    position: relative;
    overflow: hidden;
  }

  status-bar {
    display: flex;
    height: 32px;
    width: 100%;
    background: #303030;
  }
`;

const IFrame = styled.iframe`
  display: flex;
  width: 100%;
  height: auto;
  flex-grow: 1;
  border: none;
  border-top: 1px solid gray;
  transform-origin: 0 0;
  ${({ resolution }) =>
    resolution &&
    css`
      transform: ${resolution.transform};
      min-width: ${resolution.width}px;
      min-height: ${resolution.height}px;
      max-width: ${resolution.width}px;
      max-height: ${resolution.height}px;
    `}
`;

const StyledIconButton = styled(IconButton)`
  svg {
    pointer-events: none;
  }
`;

const PreviewContainer = ({
  selectedVersion,
  handleChangeVersion,
  versions,
}) => {
  const iframeContainerRef = useRef();
  const { iframeRef, startInspection, hotReloadPage } = usePreview();
  const [resolution, setResolution] = useState();
  const site = useSelector((state) => state.site);
  const isSellersTabSelected =
    useSelector((state) => state.selectedTab)?.includes("Sellers") || false;
  const active = site && selectedVersion;
  const isMasterTemplate =
    active && selectedVersion.template_version_id === "master";

  const previewUrl = useMemo(() => {
    if (!active) return false;
    return isMasterTemplate
      ? `https://preview-${site.site_id}-${
          site.current_version || site?.child_site?.current_version
        }.preview.alohome.io`
      : process.env.REACT_APP_PROXY_URL +
          `/_setVersion?version=${
            selectedVersion.template_version_id || 1
          }&sitedomain=${site?.child_site?.domain || ""}`;
  }, [isMasterTemplate, site, selectedVersion, active]);

  const sellersPreviewUrl = useMemo(() => {
    if (!active) return false;
    return isMasterTemplate
      ? `https://preview-${site.site_id}-${
          site.current_version || site?.child_site?.current_version
        }.preview.alohome.io/sellers/account/login`
      : process.env.REACT_APP_PROXY_URL + `/sellers/account/login`;
  }, [isMasterTemplate, site, active]);

  const changeResolution = useCallback(
    (width, height, zoom) => {
      const container = iframeContainerRef.current;
      const currentWidth = container.clientWidth;
      const currentHeight = container.clientHeight;

      if (width && height) {
        const scale = Math.min(currentHeight / height, currentWidth / width);
        setResolution({
          transform: `scale(${scale})`,
          width,
          height,
        });
      } else if (zoom) {
        const scale = zoom / 100;
        setResolution({
          transform: `scale(${scale})`,
          width: (currentWidth * 100) / zoom,
          height: currentHeight,
        });
      }
    },
    [iframeContainerRef]
  );

  const handleInspect = useCallback(() => startInspection(), [startInspection]);

  const handleLoad = useCallback(() => {
    if (selectedVersion && site) {
      hotReloadPage();
    }
  }, [selectedVersion, site, hotReloadPage]);

  return (
    <Container>
      <top-bar>
        <StyledIconButton size="small" onClick={handleInspect}>
          <HighlightAltIcon />
        </StyledIconButton>
        <ResponsiveDropDown onChange={changeResolution} />
        <TemplateVersionsDropDown
          versions={versions}
          value={selectedVersion}
          onChange={handleChangeVersion}
        />
      </top-bar>
      <iframe-container ref={iframeContainerRef}>
        {active &&
          (isSellersTabSelected ? (
            <IFrame
              ref={iframeRef}
              onLoad={handleLoad}
              src={sellersPreviewUrl}
              title="preview"
              resolution={resolution}
            />
          ) : (
            <IFrame
              ref={iframeRef}
              onLoad={handleLoad}
              src={previewUrl}
              title="preview"
              resolution={resolution}
            />
          ))}
      </iframe-container>
      <status-bar></status-bar>
    </Container>
  );
};

export default PreviewContainer;
